const deemphasizedPointSizeAsMarker = 5;
const deemphasizedPointSizeAsPoint = 8;

/* === BASE === */
const zoneBase = {
	fill: '#cccccc',
	fillOpacity: 0.7,
	outlineColor: '#777777',
	outlineWidth: 1,
	outlineOpacity: 0.5,
};

const zoneBaseSmall = {
	...zoneBase,
	outlineOpacity: 0.2,
};

const zoneBaseTransparent = {
	...zoneBase,
	fill: null,
	outlineWidth: 1,
	fillOpacity: 0,
	outlineOpacity: 0.5,
	outlineColor: '#777777',
};

const zoneBaseInvisible = {
	...zoneBase,
	fill: null,
	outlineWidth: 0,
	fillOpacity: 0.7,
};

const interferometryBase = {
	fill: '#cccccc',
	fillOpacity: 0.85,
	outlineColor: '#555555',
	outlineWidth: 1,
	outlineOpacity: 1,
	size: 12,
};

const interferometryBaseAsPoints = {
	...interferometryBase,
	size: 22,
	outlineWidth: 0,
};

const interferometryBaseAsPointsSmall = {
	...interferometryBaseAsPoints,
};

/* === ATTRIBUTES === */

// Points
const velAvg = {
	attributeKey: 'vel_avg',
	attributeClasses: [
		{
			fill: '#b1001d',
			name: '< -10 (Subsidence)',
			interval: [-1000, -10],
			intervalBounds: [true, false],
		},
		{
			fill: '#ca2d2f',
			interval: [-10, -8],
			intervalBounds: [true, false],
		},
		{
			fill: '#e25b40',
			interval: [-8, -6],
			intervalBounds: [true, false],
		},
		{
			fill: '#ffaa00',
			interval: [-6, -4],
			intervalBounds: [true, false],
		},
		{
			fill: '#ffff00',
			interval: [-4, -2],
			intervalBounds: [true, false],
		},
		{
			fill: '#a0f000',
			interval: [-2, 0],
			intervalBounds: [true, false],
		},
		{
			fill: '#4ce600',
			interval: [0, 2],
			intervalBounds: [true, false],
		},
		{
			fill: '#50d48e',
			interval: [2, 4],
			intervalBounds: [true, false],
		},
		{
			fill: '#00c3ff',
			interval: [4, 6],
			intervalBounds: [true, false],
		},
		{
			fill: '#0f80d1',
			interval: [6, 8],
			intervalBounds: [true, false],
		},
		{
			fill: '#004ca8',
			interval: [8, 10],
			intervalBounds: [true, false],
		},
		{
			fill: '#003e8a',
			name: '(Uplift) 10 <',
			interval: [10, 1000],
			intervalBounds: [true, false],
		},
	],
};

const losCohFilter = {
	attributeKey: 'coh',
	attributeClasses: [
		{
			interval: [-1000, 0.4],
			intervalBounds: [true, true],
			outlineColor: '#888888',
			size: deemphasizedPointSizeAsMarker,
		},
	],
};

const losCohFilterAsPoint = {
	attributeKey: 'coh',
	attributeClasses: [
		{
			interval: [-1000, 0.4],
			intervalBounds: [true, true],
			outlineColor: '#888888',
			size: deemphasizedPointSizeAsPoint,
		},
	],
};

const dtsSensFilter = {
	attributeKey: 'sens',
	attributeTransformations: ['abs'],
	attributeClasses: [
		{
			interval: [0, 0.3],
			intervalBounds: [true, false],
			outlineColor: '#888888',
			size: deemphasizedPointSizeAsMarker,
		},
	],
};

const dtsSensFilterAsPoint = {
	attributeKey: 'sens',
	attributeTransformations: ['abs'],
	attributeClasses: [
		{
			interval: [0, 0.3],
			intervalBounds: [true, false],
			outlineColor: '#888888',
			size: deemphasizedPointSizeAsPoint,
		},
	],
};

const vertggpsClusterCohFilter = {
	attributeKey: 'coh',
	attributeClasses: [
		{
			interval: [-1000, 0.7],
			intervalBounds: [true, false],
			outlineColor: '#888888',
			size: deemphasizedPointSizeAsMarker,
		},
	],
};

const vertggpsClusterCohFilterAsPoint = {
	attributeKey: 'coh',
	attributeClasses: [
		{
			interval: [-1000, 0.7],
			intervalBounds: [true, false],
			outlineColor: '#888888',
			size: deemphasizedPointSizeAsPoint,
		},
	],
};

const clDyn = {
	attributeKey: 'cl_dyn',
	attributeValues: {
		A: {
			fill: '#641a80',
		},
		DA: {
			fill: '#8c2981',
		},
		ADA: {
			fill: '#b63679',
		},
		DADA: {
			fill: '#de4969',
		},
		ADAD: {
			fill: '#f76f5c',
		},
		DAD: {
			fill: '#fe9f6d',
		},
		AD: {
			fill: '#ffb355',
		},
		D: {
			fill: '#ffff53',
		},
		CONST_TREND: {
			fill: '#a9d5aa',
		},
		NO_CLASS: {
			fill: '#ffffff',
		},
	},
};

const clClstr = {
	attributeKey: 'cl_clstr',
	attributeValues: {
		1: {
			fill: '#14c8dd',
			name: 'Cluster 1 Dormant/sinking (Slow)',
		},
		2: {
			fill: '#cd2191',
			name: 'Cluster 2 Sinking (Slow)',
		},
		3: {
			fill: '#bcc222',
			name: 'Cluster 3 Dormant/Swelling (Slow)',
		},
		4: {
			fill: '#326b09',
			name: 'Cluster 4 Sinking (Fast)',
		},
		5: {
			fill: '#ffc21b',
			name: 'Cluster 5 Swelling (Slow)',
		},
	},
};

// Polygons
const landUseClassification = {
	attributeKey: 'ua_l3',
	attributeValues: {
		11100: {
			name: 'Urban fabric: Residential - High Density Continuous',
			fill: '#a80000',
		},
		11200: {
			name: 'Urban Fabric: Residential - Discontinuous',
			fill: '#ff0000',
		},
		11300: {
			name: 'Isolated structures',
			fill: '#ffbebe',
		},
		12100: {
			name: 'Urban Fabric: Mixed non-residential (Industrial, Commercial, Public & Military Units)',
			fill: '#da5dff',
		},
		12200: {
			name: 'Arterial roads',
			fill: '#4e4e4e',
		},
		12300: {
			name: 'Airports & Ports (12300)',
			fill: '#e15989',
		},
		12400: {
			name: 'Airports & Ports (12400)',
			fill: '#e15989',
		},
		13300: {
			name: 'Construction Sites',
			fill: '#ff66de',
		},
		13400: {
			name: 'Land Without Current Use',
			fill: '#d7d7d7',
		},
		14100: {
			name: 'Green Urban Areas',
			fill: '#55ff00',
		},
		14200: {
			name: 'Sports and Leisure Facilities',
			fill: '#ffaa00',
		},
		20000: {
			name: 'Agricultural Area',
			fill: '#ffebaf',
		},
		30000: {
			name: 'Forest',
			fill: '#38a800',
		},
		40000: {
			name: 'Grasslands',
			fill: '#d5ffa5',
		},
		50000: {
			name: 'Inland Water',
			fill: '#0070ff',
		},
		70000: {
			name: 'Wetland',
			fill: '#71beab',
		},
		80000: {
			name: 'Beach',
			fill: '#ffffb1',
		},
	},
};

const exposure = {
	attributeKey: 'defo_p75',
	attributeClasses: [
		{
			fill: '#b1001d',
			name: '< -10',
			interval: [-1000, -10],
			intervalBounds: [true, false],
		},
		{
			fill: '#ca2d2f',
			interval: [-10, -8],
			intervalBounds: [true, false],
		},
		{
			fill: '#e25b40',
			interval: [-8, -6],
			intervalBounds: [true, false],
		},
		{
			fill: '#ffaa00',
			interval: [-6, -4],
			intervalBounds: [true, false],
		},
		{
			fill: '#ffff00',
			interval: [-4, -2],
			intervalBounds: [true, false],
		},
		{
			fill: '#a0f000',
			interval: [-2, 0],
			intervalBounds: [true, false],
		},
		{
			fill: '#4ce600',
			interval: [0, 2],
			intervalBounds: [true, false],
		},
		{
			fill: '#50d48e',
			interval: [2, 4],
			intervalBounds: [true, false],
		},
		{
			fill: '#00c3ff',
			interval: [4, 6],
			intervalBounds: [true, false],
		},
		{
			fill: '#0f80d1',
			interval: [6, 8],
			intervalBounds: [true, false],
		},
		{
			fill: '#004ca8',
			interval: [8, 10],
			intervalBounds: [true, false],
		},
		{
			fill: '#003e8a',
			name: '10 <',
			interval: [10, 1000],
			intervalBounds: [true, false],
		},
	],
};

const filterNonUrban = {
	attributeKey: 'ua_l3',
	attributeClasses: [
		{
			fillOpacity: 0.5,
			fill: '#ffffff',
			interval: [20000, 100000],
			intervalBounds: [true, false],
			name: 'Non-urban',
		},
	],
};

export default {
	interferometryBase,
	interferometryBaseAsPoints,
	interferometryBaseAsPointsSmall,
	zoneBase,
	zoneBaseSmall,
	zoneBaseTransparent,
	zoneBaseInvisible,

	velAvg,
	clDyn,
	clClstr,

	losCohFilter,
	losCohFilterAsPoint,
	dtsSensFilter,
	dtsSensFilterAsPoint,
	vertggpsClusterCohFilter,
	vertggpsClusterCohFilterAsPoint,

	landUseClassification,
	exposure,
	filterNonUrban,
};
